import dayjs from 'dayjs';
import React from 'react';
import s from './s.module.less';
import { Event, EventWithTimeOverlap } from 'types/calendar';
import { getEventColor, getEventColorSetting, getEventTitleDisplay, isCancelledEvent, isEventTimeOverlap } from 'utils/calendar';
import useCalendarColors from 'hooks/useCalendarColors';
import AddAndEditCalendarEvent from '../AddAndEditCalendarEvent';
import { Popover } from 'antd';
import { KlarityService, Prospect } from 'types/common';
import EventTitle from '../EventTitle';

type Props = {
    useAdminApi?: boolean
    day: dayjs.Dayjs
    events: Event[]
    onEventEdit?: (event: Event) => void,
    timezone?: string,
    onEventAdd?: (start: dayjs.Dayjs, end: dayjs.Dayjs) => void,
    pendingStartTimeForDay?: dayjs.Dayjs,
    pendingEndTimeForDay?: dayjs.Dayjs,
    onHidePopLayerForDay?: () => void,
    onEventUpdate?: () => void,
    usePopLayer?: boolean,
    allSpecialityData?: KlarityService[],
    allClients?: Prospect[],
};

const Day = (props: Props) => {
    const {
        day, events,
        onEventEdit,
        useAdminApi, timezone,
        onEventAdd,
        pendingEndTimeForDay,
        pendingStartTimeForDay,
        onHidePopLayerForDay,
        onEventUpdate,
        usePopLayer = true,
        allSpecialityData,
        allClients
    } = props;
    const [colors] = useCalendarColors({ useAdminApi });
    const [currentEvent, setCurrentEvent] = React.useState<Event>();
    const [showEdit, setShowEdit] = React.useState(false);

    const timeArray: string[] = ['0 PM', '1 AM', '2 AM', '3 AM', '4 AM', '5 AM', '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM',
        '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM', '7 PM', '8 PM', '9 PM', '10 PM', '11 PM', '0',
    ];
    const isCurrentDay = day.format('DD-MM-YY') === dayjs().format('DD-MM-YY');
    const eventsWithTimeOverlap: EventWithTimeOverlap[] = React.useMemo(() => {
        const ret: EventWithTimeOverlap[] = [];
        for (let i = 0; i < events.length; i++) {
            const currentEvent = events[i];
            const timeOverlapKeys: string[] = [];
            for (let j = 0; j < i; j++) {
                const event2 = events[j];
                if (isEventTimeOverlap(currentEvent, event2)) {
                    timeOverlapKeys.push(event2.id);
                }
            }
            ret.push({ ...events[i], timeOverlapKeys });
        }
        return ret;
    }, [events]);

    const showAddPendingArea = !!pendingStartTimeForDay;
    let top = 0;
    let height = 0;
    if (!!showAddPendingArea) {
        const startHour = pendingStartTimeForDay.hour();
        const startMin = pendingStartTimeForDay.minute();
        const endHour = pendingEndTimeForDay?.hour() || startHour;
        const endMin = pendingEndTimeForDay?.minute() || startMin;

        top = startHour * 50 + (startMin * 5) / 6;
        const bottom = endHour * 50 + (endMin * 5) / 6 + 73;
        height = 25;
    }

    const pendingAreaContent = () => {
        return (
            <div className={s.areaWrap}
                onClick={(e) => {
                    e.stopPropagation();
                }}>
                <AddAndEditCalendarEvent
                    onCancel={() => {
                        onHidePopLayerForDay?.();
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                    }}
                    onSuccess={() => {
                        onEventUpdate?.();
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                    }}
                    defaultStart={pendingStartTimeForDay}
                    defaultEnd={pendingEndTimeForDay}
                    timezone={timezone}
                    isPopLayer
                    event={currentEvent}
                    allSpecialityData={allSpecialityData}
                />
                <div
                    onClick={(e) => {
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                        onHidePopLayerForDay?.();
                    }}
                    className={s.closeIcon}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M6 18L18 6M6 6L18 18" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </div >
        )
    };

    return (
        <div className={s.wrap}>
            <div className={s.itemHeader}>
                <div className={s.header}>
                    {day.format('ddd').toUpperCase()}
                </div>
                <div><span className={isCurrentDay ? `${s.dayLabel} ${s.today}` : s.dayLabel}>{day.format('DD')}</span></div>
            </div>
            <div
                className={s.content}
            >
                <div className={s.timeWrap}>
                    <div className={s.dayBg}>
                        {timeArray.map((item) => {
                            return (
                                <div className={s.rowLine} key={item}>
                                    <span className={s.label}>{item}</span>
                                    <hr className={s.line} />
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div
                    className={s.eventWrap}
                    onClick={(e) => {
                        const offsetTop = e.nativeEvent.offsetY;
                        const targetHour = Math.floor(offsetTop / 50);
                        const left = offsetTop % 50;
                        let min = 0;
                        if (left > 12.5 && left <= 25) {
                            min = 15;
                        }
                        if (left > 25 && left <= 37.5) {
                            min = 30;
                        }
                        if (left > 37.5 && left <= 50) {
                            min = 45;
                        }
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const startTime = day.hour(targetHour).minute(min);
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        let endTime = day.hour(targetHour).minute(min).add(30, 'minute');
                        if (endTime.isAfter(dayjs(day).endOf('day'))) {
                            endTime = dayjs(day).endOf('day');
                        }
                        setCurrentEvent(undefined);
                        onEventAdd && onEventAdd(startTime, endTime);
                    }}
                >
                    {eventsWithTimeOverlap.map((evt, idx) => {
                        let startTimeDayjs = dayjs(evt.start.dateTime);
                        let endTimeDayjs = dayjs(evt.end.dateTime);
                        if (timezone) {
                            startTimeDayjs = startTimeDayjs.tz(timezone);
                            endTimeDayjs = endTimeDayjs.tz(timezone);
                        }

                        const starTimetArray = startTimeDayjs.format('HH-mm').split('-');
                        const endTimeArray = endTimeDayjs.format('HH-mm').split('-');
                        const startHour = parseInt(starTimetArray[0], 10);
                        const startMin = parseInt(starTimetArray[1], 10);
                        const endHour = parseInt(endTimeArray[0], 10);
                        const endMin = parseInt(endTimeArray[1], 10);
                        const timeLabel = `${startTimeDayjs.format('hh:mm')} - ${endTimeDayjs.format('hh:mm A')}`;

                        let top = startHour * 50 + (startMin * 5) / 6;
                        const bottom = endHour * 50 + (endMin * 5) / 6;
                        if (startHour > endHour && endHour !== 0) {
                            top = 0;
                        }
                        const cssColor = getEventColorSetting(evt, allClients);

                        const overlapCount = evt.timeOverlapKeys.length;
                        const isAccept = true; //BE not get evt.status === EventStatus.ACCEPTED || evt.status === EventStatus.CONFIRMED;
                        const isBlock = evt.type === 'blocker';
                        let height = bottom - top;
                        let narrowLayout = false;

                        if (height > 0 && height < 15) {
                            narrowLayout = true;
                        }

                        let smallLine = false;
                        if (height > 25 && height < 40) {
                            smallLine = true;
                        }

                        let className = `${s.event} ${isBlock ? s.eventBlocker : ''}`;
                        if (isCancelledEvent(evt)) {
                            className = `${className} ${s.eventCancelled}`;
                        }
                        if (smallLine) {
                            className = `${className} ${s.eventSmallLine}`;
                        }

                        if (narrowLayout) {
                            className = `${className} ${s.narrowEvent}`;
                        }
                        const isEditing = currentEvent && currentEvent.id === evt.id && showEdit;
                        if (isEditing) {
                            return (
                                <Popover
                                    open
                                    zIndex={1000}
                                    placement="bottom"
                                    title={null} content={pendingAreaContent()} arrow={true}>
                                    <div
                                        key={idx}
                                        className={className}
                                        style={{
                                            top,
                                            height: `${bottom - top}px`,
                                            right: 0,
                                            left: overlapCount * 20,
                                            color: cssColor.color,
                                            backgroundColor: cssColor.backgroundColor,
                                            border: `0.5px ${cssColor.borderStyle} ${cssColor.borderColor}`,
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (usePopLayer) {
                                                onHidePopLayerForDay?.();
                                                setCurrentEvent(evt);
                                                setShowEdit(true);
                                                return;
                                            } else {
                                                if (onEventEdit) {
                                                    onEventEdit(evt);
                                                }
                                            }
                                        }}
                                    >
                                        <p className={s.title}>
                                            <EventTitle
                                                event={evt}
                                                allSpecialityData={allSpecialityData}
                                            />
                                            {/* {getEventTitleDisplay(evt) || 'No title'} */}
                                        </p>
                                        <p className={s.time}>{timeLabel}</p>
                                    </div>
                                </Popover>
                            )
                        }
                        return (
                            <div
                                key={idx}
                                className={className}
                                style={{
                                    top,
                                    height: `${bottom - top}px`,
                                    right: 0,
                                    left: overlapCount * 20,
                                    color: cssColor.color,
                                    backgroundColor: cssColor.backgroundColor,
                                    border: `0.5px ${cssColor.borderStyle} ${cssColor.borderColor}`,
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (usePopLayer) {
                                        onHidePopLayerForDay?.();
                                        setCurrentEvent(evt);
                                        setShowEdit(true);
                                        return;
                                    } else {
                                        if (onEventEdit) {
                                            onEventEdit(evt);
                                        }
                                    }
                                }}
                            >
                                <p className={s.title}>
                                    <EventTitle
                                        event={evt}
                                        allSpecialityData={allSpecialityData}
                                    />
                                    {/* {getEventTitleDisplay(evt) || 'No title'} */}
                                </p>
                                <p className={s.time}>{timeLabel}</p>
                            </div>
                        );
                    })}
                    {
                        showAddPendingArea && (
                            <Popover
                                open
                                placement="bottom"
                                title={null}
                                zIndex={1000}
                                content={pendingAreaContent()}
                                arrow={true}>
                                <div
                                    className={s.event}
                                    style={{
                                        top,
                                        height: `${height}px`,
                                        right: 24,
                                        left: 0,
                                    }}
                                    onClick={() => {

                                    }}
                                >
                                    <p className={s.title}>
                                        {/* No title */}
                                    </p>
                                </div>
                            </Popover>
                        )
                    }
                </div>
            </div>
        </div>
    );
};

export default Day;
